import { useCallback, useEffect, useMemo, useRef, useState } from "react";

import { buildStakeholderDuplicatesPayload } from "src/core/builders/stakeholderDuplicates.builder";
import { AdvancedPersonWithEReputationStatus, MappedStakeholder } from "src/core/constants/folder";
import { useFoldersUsingEntityLazyQuery } from "src/core/gql/graphql";
import { useCountryWithRiskOptions } from "src/core/hooks/common/useCountryWithRiskOptions";
import { useResolveStakeholderDuplicates } from "src/core/hooks/folder/stakeholder-duplicates/useResolveStakeholderDuplicates";

type CustomerCompaniesByEntity = Record<
  string,
  {
    isLoading: boolean;
    companyNames: string[];
  }
>;

type UseStakeholderDuplicatesModalProps = {
  open: boolean;
  stakeholder: MappedStakeholder;
};

/*
 * Hook de gestion de la modale de résolution des doublons de stakeholders.
 *
 * Responsabilités :
 * - piloter l'état local de la modale (pagination, décisions utilisateur, profil de référence) ;
 * - charger les sociétés clientes associées aux entités visibles ;
 * - construire le payload attendu pour la résolution des doublons ;
 * - déclencher la résolution via le hook métier dédié.
 */
export const useStakeholderDuplicatesModal = ({
  open,
  stakeholder,
}: UseStakeholderDuplicatesModalProps) => {
  const { countryWithRiskOptions } = useCountryWithRiskOptions();

  const { resolveStakeholderDuplicates } = useResolveStakeholderDuplicates();

  const [foldersUsingEntity] = useFoldersUsingEntityLazyQuery({
    fetchPolicy: "no-cache",
  });

  /**
   * Etat de chargement de l'action de résolution des doublons.
   */
  const [loading, setLoading] = useState<boolean>(false);

  /**
   * Cache local des sociétés clientes connues par entité.
   * La clé est l'id de l'entité (stakeholder courant ou candidat doublon).
   *
   * On conserve :
   * - l'état de chargement par entité ;
   * - la liste dédupliquée des raisons sociales associées.
   */
  const [customerCompaniesByEntity, setCustomerCompaniesByEntity] =
    useState<CustomerCompaniesByEntity>({});

  /**
   * Index de départ des candidats actuellement affichés.
   * La modale n'affiche que 2 candidats à la fois.
   */
  const [candidateStartIndex, setCandidateStartIndex] = useState<number>(0);

  /**
   * Décision utilisateur par candidat :
   * - "same"      => considéré comme doublon du stakeholder courant
   * - "different" => considéré comme distinct
   *
   * La clé est construite via getCandidateDecisionKey pour rester stable
   * même si un candidat n'a pas d'id exploitable.
   */
  const [candidateDecisions, setCandidateDecisions] = useState<
    Record<string, "different" | "same">
  >({});

  /**
   * Profil de référence sélectionné par l'utilisateur.
   *
   * - "current"   => le stakeholder courant devient la source de vérité ;
   * - "candidate" => l'un des candidats "same" devient la source de vérité ;
   * - null        => aucun profil de référence encore choisi.
   */
  const [selectedReferenceProfile, setSelectedReferenceProfile] = useState<
    { type: "current" } | { type: "candidate"; key: string } | null
  >(null);

  /**
   * Mémoire des entités déjà chargées pour éviter de relancer inutilement
   * la query foldersUsingEntity à chaque rerender / navigation.
   *
   * Ce ref n'entraîne pas de re-render lorsqu'il est modifié.
   */
  const fetchedEntityIdsRef = useRef<Set<string>>(new Set());

  /**
   * Liste complète des candidats doublons disponibles pour le stakeholder courant.
   */
  const candidates = useMemo(
    () => stakeholder.duplicateCandidates ?? [],
    [stakeholder.duplicateCandidates]
  );

  /**
   * Index maximal autorisé pour la pagination locale des candidats.
   * Comme on affiche 2 cartes à la fois, on borne l'index de départ.
   */
  const maxStartIndex = Math.max(0, candidates.length - 2);

  /**
   * Sous-ensemble des candidats actuellement visibles dans la modale.
   */
  const visibleCandidates = useMemo(
    () => candidates.slice(candidateStartIndex, candidateStartIndex + 2),
    [candidates, candidateStartIndex]
  );

  /**
   * Indicateurs de navigation pour l'affichage des contrôles précédent / suivant.
   */
  const canGoPrev = candidates.length > 2 && candidateStartIndex > 0;
  const canGoNext = candidates.length > 2 && candidateStartIndex < maxStartIndex;

  /**
   * Construit une clé stable pour stocker les décisions utilisateur.
   *
   * On privilégie l'id du candidat lorsqu'il existe.
   * Sinon, on retombe sur une clé dérivée de l'id du stakeholder courant
   * et de l'index absolu du candidat.
   */
  const getCandidateDecisionKey = useCallback(
    (candidate: AdvancedPersonWithEReputationStatus, absoluteIndex: number) =>
      candidate.id ?? `${stakeholder.id}-${absoluteIndex}`,
    [stakeholder.id]
  );

  /**
   * Indique s'il existe au moins un candidat marqué comme "same".
   * Condition nécessaire pour autoriser la sauvegarde.
   */
  const hasSameCandidate = useMemo(
    () => Object.values(candidateDecisions).some((decision) => decision === "same"),
    [candidateDecisions]
  );

  /**
   * Liste des candidats effectivement retenus comme doublons.
   * Ce sont les seuls candidats envoyés dans le payload de résolution.
   */
  const selectedDuplicateCandidates = useMemo(
    () =>
      candidates.filter(
        (candidate, absoluteIndex) =>
          candidateDecisions[getCandidateDecisionKey(candidate, absoluteIndex)] === "same"
      ),
    [candidateDecisions, candidates, getCandidateDecisionKey]
  );

  /**
   * Table de correspondance clé -> candidat.
   * Permet de reconstruire facilement le profil de référence sélectionné
   * lorsque celui-ci pointe vers un candidat.
   */
  const candidateByDecisionKey = useMemo(() => {
    return candidates.reduce<Record<string, AdvancedPersonWithEReputationStatus>>(
      (acc, candidate, i) => {
        acc[getCandidateDecisionKey(candidate, i)] = candidate;
        return acc;
      },
      {}
    );
  }, [candidates, getCandidateDecisionKey]);

  /**
   * Navigue vers le candidat précédent.
   */
  const goToPreviousCandidate = useCallback(() => {
    setCandidateStartIndex((current) => Math.max(0, current - 1));
  }, []);

  /**
   * Navigue vers le candidat suivant.
   */
  const goToNextCandidate = useCallback(() => {
    setCandidateStartIndex((current) => Math.min(maxStartIndex, current + 1));
  }, [maxStartIndex]);

  /**
   * Marque explicitement un candidat comme différent.
   * Ce candidat ne sera pas inclus dans la résolution des doublons.
   */
  const markCandidateAsDifferent = useCallback(
    (candidate: AdvancedPersonWithEReputationStatus, absoluteIndex: number) => {
      const key = getCandidateDecisionKey(candidate, absoluteIndex);
      setCandidateDecisions((current) => ({ ...current, [key]: "different" }));
    },
    [getCandidateDecisionKey]
  );

  /**
   * Marque un candidat comme doublon du stakeholder courant.
   *
   * Par convention UX actuelle, lorsqu'un candidat est marqué comme "same",
   * le stakeholder courant devient automatiquement le profil de référence
   * par défaut. L'utilisateur peut ensuite modifier ce choix manuellement.
   */
  const markCandidateAsSame = useCallback(
    (candidate: AdvancedPersonWithEReputationStatus, absoluteIndex: number) => {
      const key = getCandidateDecisionKey(candidate, absoluteIndex);
      setCandidateDecisions((current) => ({ ...current, [key]: "same" }));
      setSelectedReferenceProfile({ type: "current" });
    },
    [getCandidateDecisionKey]
  );

  /**
   * Sélectionne explicitement le stakeholder courant comme profil de référence.
   */
  const selectCurrentAsReference = useCallback(() => {
    setSelectedReferenceProfile({ type: "current" });
  }, []);

  /**
   * Sélectionne un candidat comme profil de référence.
   * Ce choix n'est cohérent que si le candidat est déjà marqué comme "same".
   * Un useEffect plus bas garantit d'ailleurs cette cohérence.
   */
  const selectCandidateAsReference = useCallback(
    (candidate: AdvancedPersonWithEReputationStatus, absoluteIndex: number) => {
      setSelectedReferenceProfile({
        type: "candidate",
        key: getCandidateDecisionKey(candidate, absoluteIndex),
      });
    },
    [getCandidateDecisionKey]
  );

  /**
   * Réinitialise l'état interactif de la modale à chaque ouverture
   * ou lors d'un changement de stakeholder.
   *
   * Objectif :
   * - repartir d'une pagination propre ;
   * - oublier les décisions précédentes ;
   * - forcer une nouvelle sélection de profil de référence.
   */
  useEffect(() => {
    if (!open) return;
    setCandidateStartIndex(0);
    setCandidateDecisions({});
    setSelectedReferenceProfile(null);
  }, [open, stakeholder.id]);

  /**
   * Lorsque la modale se ferme, on purge :
   * - les entités déjà chargées ;
   * - le cache local des sociétés clientes.
   *
   * Cela permet de repartir d'un état propre à la prochaine ouverture.
   */
  useEffect(() => {
    if (!open) {
      fetchedEntityIdsRef.current.clear();
      setCustomerCompaniesByEntity({});
    }
  }, [open]);

  /**
   * Garantit que l'index courant reste valide si la liste de candidats change.
   * Evite de conserver un index hors bornes.
   */
  useEffect(() => {
    setCandidateStartIndex((current) => Math.min(current, maxStartIndex));
  }, [maxStartIndex]);

  /**
   * Charge les sociétés clientes pour :
   * - le stakeholder courant ;
   * - les candidats actuellement visibles.
   *
   * On limite volontairement le chargement aux cartes visibles afin d'éviter
   * des requêtes inutiles sur l'ensemble des candidats.
   *
   * Le ref fetchedEntityIdsRef sert à empêcher les doublons de chargement.
   */
  useEffect(() => {
    if (!open) return;

    const visibleStakeholders = [
      { role: "current", entityId: stakeholder.id, sourceIndex: null as number | null },
      ...visibleCandidates.map((candidate, index) => ({
        role: "candidate",
        entityId: candidate.id,
        sourceIndex: candidateStartIndex + index,
      })),
    ];

    for (const visibleStakeholder of visibleStakeholders) {
      const { entityId, role, sourceIndex } = visibleStakeholder;
      if (!entityId || fetchedEntityIdsRef.current.has(entityId)) continue;

      fetchedEntityIdsRef.current.add(entityId);
      setCustomerCompaniesByEntity((current) => ({
        ...current,
        [entityId]: {
          isLoading: true,
          companyNames: current[entityId]?.companyNames ?? [],
        },
      }));

      void foldersUsingEntity({
        variables: {
          entityId,
        },
      })
        .then((response) => {
          const companyNames = Array.from(
            new Set(
              (response.data?.foldersUsingEntity?.summaries ?? [])
                .map((summary) => summary.customerFolder?.customer?.name?.stringValue?.trim() ?? "")
                .filter((name) => name.length > 0)
            )
          );

          setCustomerCompaniesByEntity((current) => ({
            ...current,
            [entityId]: {
              isLoading: false,
              companyNames,
            },
          }));
        })
        .catch((error) => {
          setCustomerCompaniesByEntity((current) => ({
            ...current,
            [entityId]: {
              isLoading: false,
              companyNames: [],
            },
          }));

          console.error("[DuplicateCheckModal] foldersUsingEntity failed", {
            role,
            entityId,
            sourceIndex,
            error,
          });
        });
    }
  }, [candidateStartIndex, foldersUsingEntity, open, stakeholder.id, visibleCandidates]);

  /**
   * Maintient la cohérence du profil de référence sélectionné.
   *
   * Cas couverts :
   * - si plus aucun candidat n'est marqué "same", le profil "current" n'a plus lieu d'être ;
   * - si un candidat de référence n'est plus marqué "same", il est automatiquement désélectionné.
   */
  useEffect(() => {
    if (!selectedReferenceProfile) return;

    if (selectedReferenceProfile.type === "current" && !hasSameCandidate) {
      setSelectedReferenceProfile(null);
      return;
    }

    if (selectedReferenceProfile.type === "candidate") {
      const selectedCandidateDecision = candidateDecisions[selectedReferenceProfile.key];
      if (selectedCandidateDecision !== "same") {
        setSelectedReferenceProfile(null);
      }
    }
  }, [candidateDecisions, hasSameCandidate, selectedReferenceProfile]);

  /**
   * Construit le payload métier et déclenche la résolution effective des doublons.
   *
   * Ce callback :
   * - ne gère pas les toasts ;
   * - ne gère pas les traductions ;
   * - ne ferme pas la modale ;
   *
   * Ces responsabilités restent volontairement dans le composant afin de
   * séparer la logique métier/état local de la logique de présentation.
   */
  const resolveDuplicateSelection = useCallback(async () => {
    if (loading || !hasSameCandidate) return;

    const payload = buildStakeholderDuplicatesPayload({
      stakeholderId: stakeholder.id,
      selectedDuplicateCandidates,
      selectedReferenceProfile,
      candidateByDecisionKey,
    });

    setLoading(true);

    try {
      await resolveStakeholderDuplicates(payload);
    } finally {
      setLoading(false);
    }
  }, [
    candidateByDecisionKey,
    loading,
    hasSameCandidate,
    resolveStakeholderDuplicates,
    selectedDuplicateCandidates,
    stakeholder.id,
    selectedReferenceProfile,
  ]);

  return {
    loading,
    countryWithRiskOptions,
    visibleCandidates,
    canGoPrev,
    canGoNext,
    hasSameCandidate,
    selectedReferenceProfile,
    customerCompaniesByEntity,
    getCandidateDecisionKey,
    resolveDuplicateSelection,
    candidateStartIndex,
    candidates,
    candidateDecisions,
    goToPreviousCandidate,
    goToNextCandidate,
    markCandidateAsSame,
    markCandidateAsDifferent,
    selectCurrentAsReference,
    selectCandidateAsReference,
  };
};
